import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle } from "lucide-react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import {
  createPreviewActions,
  MutationConfirmPreview,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { TransactionDto } from "@/features/wallet/api/dto";
import { useConfirmLedger } from "@/features/wallet/api/use-confirm-ledger";
import { useMarkFailed } from "@/features/wallet/api/use-mark-failed";
import { useTransactionsTranslation } from "@/features/wallet/i18n/use-transactions-translation";
import { createMarkFailedSchema } from "@/features/wallet/schemas/transaction.schema";

/**
 * Boutons d'orchestration du flux §5.3 (Kafka non câblé — appels HTTP
 * explicites). Visibles uniquement aux rôles flashpay-service /
 * flashpay-admin / flashpay-superadmin (décision plan n°3).
 */
export function TransactionOrchestrationActions({
  transaction,
}: {
  transaction: TransactionDto;
}) {
  const { t } = useTransactionsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const markFailedSchema = useMemo(() => createMarkFailedSchema(), [i18n.language]);

  const confirmLedger = useConfirmLedger(transaction.id);
  const markFailed = useMarkFailed(transaction.id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [markFailedOpen, setMarkFailedOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();

  // L'orchestration n'a de sens que sur un statut transitoire.
  if (transaction.status !== "AWAITING_LEDGER" && transaction.status !== "PENDING") {
    return null;
  }

  const pending = confirmLedger.isPending || markFailed.isPending;

  const onConfirmLedger = () =>
    confirmLedger.mutate(undefined, {
      onSuccess: () => {
        notifSilence(t("wallet.transactions.feedback.ledgerConfirmed"));
        setConfirmOpen(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });

  const onMarkFailed = () => {
    const parsed = markFailedSchema.safeParse({ reason });
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0]?.message);
      return;
    }
    markFailed.mutate(
      { reason: parsed.data.reason },
      {
        onSuccess: () => {
          notifSilence(t("wallet.transactions.feedback.markedFailed"));
          setMarkFailedOpen(false);
          setReason("");
          setReasonError(undefined);
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      }
    );
  };

  return (
    <RequireRole
      roles={[
        FLASHPAY_ROLES.SERVICE,
        FLASHPAY_ROLES.SUPERADMIN,
        FLASHPAY_ROLES.ADMIN,
      ]}
    >
    <div className="flex flex-wrap items-center gap-2">
      <Button onClick={() => setConfirmOpen(true)} disabled={pending}>
        {confirmLedger.isPending ? (
          <ButtonSpinner />
        ) : (
          <CheckCircle className="mr-2 h-4 w-4" />
        )}
        {t("wallet.transactions.orchestration.confirmLedger")}
      </Button>
      <Button
        variant="destructive"
        onClick={() => setMarkFailedOpen(true)}
        disabled={pending}
      >
        <XCircle className="mr-2 h-4 w-4" />
        {t("wallet.transactions.orchestration.markFailed")}
      </Button>

      <MutationConfirmPreview
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        data={{
          id: transaction.id,
          reference: transaction.reference,
          status: transaction.status,
          amountMinor: transaction.amountMinor,
          currency: transaction.currency,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("wallet.transactions.orchestration.confirmLedger")}
        t={t}
        pending={confirmLedger.isPending}
        onConfirm={onConfirmLedger}
      />

      {markFailedOpen ? (
        <ShareablePreview
          data={{
            id: transaction.id,
            reference: transaction.reference,
            status: transaction.status,
          }}
          title={t("wallet.transactions.orchestration.markFailedTitle")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !markFailed.isPending) setMarkFailedOpen(false);
          }}
          maxHeight="90vh"
          customView={() => (
            <div className="space-y-1.5">
              <Label htmlFor="reason">
                {t("wallet.transactions.orchestration.reason")}
              </Label>
              <Textarea
                id="reason"
                placeholder={t(
                  "wallet.transactions.orchestration.reasonPlaceholder"
                )}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError(undefined);
                }}
              />
              {reasonError ? (
                <p className="text-sm text-destructive">{reasonError}</p>
              ) : null}
            </div>
          )}
          actions={createPreviewActions({
            onConfirm: onMarkFailed,
            onCancel: () => setMarkFailedOpen(false),
            t,
            labels: {
              confirm: t("wallet.transactions.orchestration.markFailed"),
            },
            confirmLoading: markFailed.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </div>
    </RequireRole>
  );
}
